// src/pages/Dashboard/DashboardSection.js
import React, { useState, useEffect } from "react"
import { Card, CardBody, Row, Col, Button, Input, Label } from "reactstrap"
import Flatpickr from "react-flatpickr"
import "flatpickr/dist/themes/material_blue.css"
import { SlCalender } from "react-icons/sl"

const DashboardSection = ({ onApply, onReset, currentFilters = {} }) => {
  const [dateRange, setDateRange] = useState(() => {
    if (currentFilters.startDate && currentFilters.endDate) {
      return [
        new Date(currentFilters.startDate),
        new Date(currentFilters.endDate),
      ]
    }
    return [null, null]
  })

  const [mobileNumbersInput, setMobileNumbersInput] = useState(
    currentFilters.mobileNumbers?.join(", ") || ""
  )

  useEffect(() => {
    if (currentFilters.startDate && currentFilters.endDate) {
      setDateRange([
        new Date(currentFilters.startDate),
        new Date(currentFilters.endDate),
      ])
    } else {
      setDateRange([null, null])
    }
    setMobileNumbersInput(currentFilters.mobileNumbers?.join(", ") || "")
  }, [currentFilters])

  // ─── Helper: Check if we have a valid complete date range ───
  const hasValidDateRange = () => {
    if (!Array.isArray(dateRange) || dateRange.length !== 2) return false
    const [start, end] = dateRange

    // Important: check both are valid Date objects and not null/undefined
    if (!(start instanceof Date) || !(end instanceof Date)) return false
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return false

    return start <= end
  }

  const hasMobileInput = mobileNumbersInput.trim().length > 0

  // Apply button ENABLED agar KUCH BHI filter laga ho
  const isApplyDisabled = !hasValidDateRange() && !hasMobileInput

  const handleApply = () => {
    let startStr = null
    let endStr = null

    if (hasValidDateRange()) {
      const [start, end] = dateRange
      startStr = `${start.getFullYear()}-${String(
        start.getMonth() + 1
      ).padStart(2, "0")}-${String(start.getDate()).padStart(2, "0")}`
      endStr = `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(
        2,
        "0"
      )}-${String(end.getDate()).padStart(2, "0")}`
    }

    const mobiles = mobileNumbersInput
      .split(",")
      .map(num => num.trim())
      .filter(num => num.length === 10 && /^\d{10}$/.test(num))

    onApply({
      startDate: startStr,
      endDate: endStr,
      mobileNumbers: mobiles,
    })
  }

  const handleReset = () => {
    setDateRange([null, null])
    setMobileNumbersInput("")
    onReset()
  }

  return (
    <Card className="shadow border-0 rounded-3 overflow-hidden mb-4">
      <CardBody className="p-4">
        <div className="d-flex align-items-center mb-4">
          <div className="avatar-sm bg-soft-primary rounded-circle d-flex align-items-center justify-content-center me-3">
            <i className="bx bx-filter-alt fs-4 text-primary"></i>
          </div>
          <h4 className="mb-0 fw-semibold">Dashboard Filters</h4>
        </div>

        <Row className="g-4 align-items-end">
          <Col lg="auto" md="auto" sm={12}>
            <Label className="form-label fw-medium d-block mb-2">
              Date Range
            </Label>
            <div
              className="input-group"
              style={{ minWidth: "240px", maxWidth: "280px" }}
            >
              <Flatpickr
                className="form-control border-0 dash-filter-picker shadow-sm"
                placeholder="Select date range"
                options={{
                  mode: "range",
                  dateFormat: "d M, Y",
                  maxDate: "today",
                }}
                value={dateRange} // ← important: controlled value
                onChange={selectedDates => {
                  setDateRange(selectedDates) // selectedDates = [Date, Date] or []
                }}
              />
              <div className="input-group-text border-0 text-white bg-primary">
                <SlCalender />
              </div>
            </div>
          </Col>

          <Col lg={4} md={6} sm={12}>
            <Label className="form-label fw-medium d-block mb-2">
              Mobile Numbers (comma separated, 10 digits each)
            </Label>
            <Input
              type="text"
              className="form-control shadow-sm"
              placeholder="e.g. 9876543210, 9123456789"
              value={mobileNumbersInput}
              onChange={e => setMobileNumbersInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter") {
                  e.preventDefault()
                  handleApply()
                }
              }}
            />
          </Col>

          <Col lg="auto" md="auto" sm={12} className="d-flex gap-2">
            <Button
              color="primary"
              className="px-4"
              onClick={handleApply}
              disabled={isApplyDisabled}
            >
              <i className="bx bx-search me-1"></i> Apply
            </Button>

            <Button
              color="danger"
              outline
              className="px-4"
              onClick={handleReset}
            >
              <i className="bx bx-x me-1"></i> Reset
            </Button>
          </Col>
        </Row>

        {(hasValidDateRange() || hasMobileInput) && (
          <div className="mt-3 text-muted small">
            {hasValidDateRange() && (
              <span>
                Date: {dateRange[0].toLocaleDateString("en-IN")} –{" "}
                {dateRange[1].toLocaleDateString("en-IN")}
              </span>
            )}
            {hasValidDateRange() && hasMobileInput && " | "}
            {hasMobileInput && (
              <span>
                Mobile:{" "}
                {
                  mobileNumbersInput
                    .split(",")
                    .map(n => n.trim())
                    .filter(Boolean).length
                }{" "}
                number
                {mobileNumbersInput.split(",").filter(n => n.trim()).length !==
                1
                  ? "s"
                  : ""}
              </span>
            )}
          </div>
        )}
      </CardBody>
    </Card>
  )
}

export default DashboardSection
